import { useState } from "react";
import { MapPin, CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import { getValidationStatusIcon } from "../../../utils/validationUtils.js";

export default function CountiesValidation({ validation }) {
    const [expandedSections, setExpandedSections] = useState({
        expected: false,
        matched: false,
        missing: false,
        unexpected: false
    });

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const getScoreColor = (score) => {
        if (score >= 90) return 'bg-emerald-100 text-emerald-700 border-emerald-200';
        if (score >= 75) return 'bg-blue-100 text-blue-700 border-blue-200';
        if (score >= 50) return 'bg-amber-100 text-amber-700 border-amber-200';
        return 'bg-red-100 text-red-700 border-red-200';
    };

    const matchCount = validation.matches?.length || 0;
    const missingCount = validation.missing?.length || 0;
    const unexpectedCount = validation.unexpected?.length || 0;

    return (
        <div className="border-l-4 border-l-teal-500 border-t border-r border-b border-slate-200 rounded-lg overflow-hidden">
            {/* Compact Header with Summary */}
            <div className="bg-teal-50/30 p-3 border-b border-slate-200">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <MapPin size={16} className="text-blue-600" />
                        <h5 className="font-medium text-slate-900">Counties Coverage</h5>
                        {getValidationStatusIcon(validation.status, validation.score)}
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Quick Stats Badges */}
                        <div className="flex items-center gap-2 text-xs">
                            {matchCount > 0 && (
                                <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full font-medium">
                                    ✓ {matchCount}
                                </span>
                            )}
                            {missingCount > 0 && (
                                <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full font-medium">
                                    ✗ {missingCount}
                                </span>
                            )}
                            {unexpectedCount > 0 && (
                                <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full font-medium">
                                    ⚠ {unexpectedCount}
                                </span>
                            )}
                        </div>
                        <div className={`px-3 py-1 rounded-full text-sm font-semibold border ${getScoreColor(validation.score)}`}>
                            {validation.score}%
                        </div>
                    </div>
                </div>
            </div>

            {/* Collapsible Content Sections */}
            <div className="divide-y divide-slate-100">
                {/* Expected Counties */}
                {validation.expectedCounties && (
                    <div>
                        <button
                            onClick={() => toggleSection('expected')}
                            className="w-full px-3 py-2 flex items-center justify-between hover:bg-blue-50 transition-colors"
                        >
                            <div className="flex items-center gap-2">
                                {expandedSections.expected ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                <MapPin size={14} className="text-blue-600" />
                                <span className="text-sm font-medium text-blue-700">Expected Counties</span>
                            </div>
                        </button>
                        {expandedSections.expected && (
                            <div className="px-3 pb-3 bg-blue-50/30">
                                <div className="text-xs bg-white p-2 rounded border border-blue-200 text-slate-700">
                                    {validation.expectedCounties}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Matched Counties */}
                {matchCount > 0 && (
                    <div>
                        <button
                            onClick={() => toggleSection('matched')}
                            className="w-full px-3 py-2 flex items-center justify-between hover:bg-emerald-50 transition-colors"
                        >
                            <div className="flex items-center gap-2">
                                {expandedSections.matched ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                <CheckCircle2 size={14} className="text-emerald-600" />
                                <span className="text-sm font-medium text-emerald-700">Matched Counties</span>
                                <span className="text-xs text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">{matchCount}</span>
                            </div>
                        </button>
                        {expandedSections.matched && (
                            <div className="px-3 pb-3 bg-emerald-50/30">
                                <div className="flex flex-wrap gap-1.5">
                                    {validation.matches.map((county, i) => (
                                        <span
                                            key={i}
                                            className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-1 rounded text-xs font-medium"
                                        >
                                            <CheckCircle2 size={12} />
                                            {county}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Missing Counties */}
                {missingCount > 0 && (
                    <div>
                        <button
                            onClick={() => toggleSection('missing')}
                            className="w-full px-3 py-2 flex items-center justify-between hover:bg-red-50 transition-colors"
                        >
                            <div className="flex items-center gap-2">
                                {expandedSections.missing ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                <XCircle size={14} className="text-red-600" />
                                <span className="text-sm font-medium text-red-700">Missing Counties</span>
                                <span className="text-xs text-red-600 bg-red-100 px-2 py-0.5 rounded-full">{missingCount}</span>
                            </div>
                        </button>
                        {expandedSections.missing && (
                            <div className="px-3 pb-3 bg-red-50/30">
                                <div className="text-xs text-slate-600 mb-2 px-2">Expected but not found on page</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {validation.missing.map((county, i) => (
                                        <span
                                            key={i}
                                            className="inline-flex items-center gap-1 bg-red-50 text-red-700 border border-red-200 px-2 py-1 rounded text-xs font-medium"
                                        >
                                            <XCircle size={12} />
                                            {county}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Unexpected Counties */}
                {unexpectedCount > 0 && (
                    <div>
                        <button
                            onClick={() => toggleSection('unexpected')}
                            className="w-full px-3 py-2 flex items-center justify-between hover:bg-amber-50 transition-colors"
                        >
                            <div className="flex items-center gap-2">
                                {expandedSections.unexpected ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                <AlertTriangle size={14} className="text-amber-600" />
                                <span className="text-sm font-medium text-amber-700">Unexpected Counties</span>
                                <span className="text-xs text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full">{unexpectedCount}</span>
                            </div>
                        </button>
                        {expandedSections.unexpected && (
                            <div className="px-3 pb-3 bg-amber-50/30">
                                <div className="text-xs text-slate-600 mb-2 px-2">Found but not in expected list</div>
                                <div className="flex flex-wrap gap-1.5">
                                    {validation.unexpected.map((county, i) => (
                                        <span
                                            key={i}
                                            className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-amber-200 px-2 py-1 rounded text-xs font-medium"
                                        >
                                            <AlertTriangle size={12} />
                                            {county}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Empty State */}
            {matchCount === 0 && missingCount === 0 && unexpectedCount === 0 && !validation.expectedCounties && (
                <div className="p-4 text-center text-sm text-slate-500">
                    No county information available
                </div>
            )}
        </div>
    );
}
